import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Caption from 'LuxKit/Typography/Caption'
import { ViewOfferButton } from './ViewOfferButton'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import type { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'

interface Props {
  isFlash?: boolean
  roomOccupancy?: string
  priceDetails: App.Pricing
  memberPrice?: number
  memberPriceType?: App.Cruises.CruiseMemberPriceType
  isPriceLoading?: boolean
  className?: string
  offerId: string
  variant?: 'default' | 'condensed'
  filters?: App.OfferListFilters
  loyaltyProductType?: App.LoyaltyCruiseProductType
  showViewButton?: boolean
  promotionalPrice?: CruiseOfferPriceVariation['promotionalPrice']
  cabinCategory?: string
}

function CruisePriceDetails(props: Props) {
  const intl = useIntl()
  const {
    variant = 'default',
    priceDetails,
    memberPrice,
    memberPriceType,
    isPriceLoading,
    isFlash,
    roomOccupancy,
    offerId,
    filters,
    loyaltyProductType,
    showViewButton = true,
    promotionalPrice,
    cabinCategory,
  } = props
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const { priceInPoints, memberPriceInPoints, pointsToBurn, pointsOnly, hideZeroCash, shouldShowBurn } = useSearchOfferBurnPricing({
    price: priceDetails.price,
    memberPrice,
    loyaltyProductType,
    viewPriceInPoints: filters?.viewPriceInPoints,
  })

  return (
    <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical" gap={8}>
        <div>
          {isPriceLoading && <>
            <PriceRowPriceCaptionLoadingSkeleton width="4ch" />
            <PriceRowLuxPlusPriceLoadingSkeleton size="L" />
          </>}
          {!isPriceLoading && promotionalPrice && <>
            <PriceRowPriceCaption>{cabinTypeLabels[cabinCategory ?? ''] ?? cabinCategory}</PriceRowPriceCaption>
            <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack size="L" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />
              <PriceRowCaption><CruisePromotionPriceUnit promotion={promotionalPrice.promotion} /></PriceRowCaption>
            </Group>
            {promotionalPrice.perPersonPrice !== undefined && <Caption variant="medium" weight="bold" colour="success-dark">
              <FormattedMessage
                id="UDli10X2x9NsPnjIDvB0"
                defaultMessage="{price}/person"
                description="Per-person equivalent shown under a fixed cruise promotional total price"
                values={{ price: <FormatCurrency value={promotionalPrice.perPersonPrice} /> }}
              />
            </Caption>}
          </>}
          {!isPriceLoading && !promotionalPrice && <>
            <PriceRowPriceCaption>{intl.formatMessage({
              id: 'U066vyS-v-9t51qBNa66',
              defaultMessage: 'From',
              description: 'Price caption label preceding the cruise price in the cruises section',
            })}
            </PriceRowPriceCaption>
            {shouldShowBurn && memberPriceType !== 'STANDARD_PRICE' && <LoyaltyBurnPriceDisplay
              totalPrice={priceDetails.price}
              pointsToBurn={pointsToBurn}
              priceInPoints={priceInPoints}
              memberPrice={memberPrice}
              memberPriceInPoints={memberPriceInPoints}
              size="L"
              saleUnit={priceDetails.saleUnit}
              pointsOnly={pointsOnly}
              hideZeroCash={hideZeroCash}
            />}
            {(!shouldShowBurn || memberPriceType === 'STANDARD_PRICE') && <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack
                size="L"
                price={priceDetails.price}
                memberPrice={memberPrice}
                saleUnit={priceDetails.saleUnit}
                mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
              />
            </Group>}
          </>}
          {!isPriceLoading && !!priceDetails.value && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={priceDetails.value}
            discountPercentage={priceDetails.discountPercent}
          />}
          {isFlash && !!roomOccupancy && <PriceRowCaption>{roomOccupancy}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          productType={isFlash ? 'cruise-flash' : 'cruise'}
          offerId={offerId}
        />
      </Group>
      {showViewButton && <ViewOfferButton variant={variant} />}
    </Group>
  )
}

export default CruisePriceDetails
